<script lang="ts">
import type { ButtonHTMLAttributes, HTMLAttributes } from 'vue'
import type { RouteLocationRaw, RouterLinkProps } from 'vue-router'
</script>

<script setup lang="ts" generic="T extends FooterColumnLink">
import { Primitive } from 'reka-ui'

export interface NuxtLinkProps extends Omit<RouterLinkProps, 'to'> {
  /**
   * Route Location the link should navigate to when clicked on.
   */
  to?: RouteLocationRaw // need to manually type to avoid breaking typedPages
  /**
   * An alias for `to`. If used with `to`, `href` will be ignored
   */
  href?: NuxtLinkProps['to']
  /**
   * Forces the link to be considered as external (true) or internal (false). This is helpful to handle edge-cases
   */
  external?: boolean
  /**
   * Where to display the linked URL, as the name for a browsing context.
   */
  target?: '_blank' | '_parent' | '_self' | '_top' | (string & {}) | null
  /**
   * A rel attribute value to apply on the link. Defaults to "noopener noreferrer" for external links.
   */
  rel?: 'noopener' | 'noreferrer' | 'nofollow' | 'sponsored' | 'ugc' | (string & {}) | null
  /**
   * If set to true, no rel attribute will be added to the link
   */
  noRel?: boolean
  /**
   * A class to apply to links that have been prefetched.
   */
  prefetchedClass?: string
  /**
   * When enabled will prefetch middleware, layouts and payloads of links in the viewport.
   */
  prefetch?: boolean
  /**
   * Allows controlling when to prefetch links. By default, prefetch is triggered only on visibility.
   */
  prefetchOn?: 'visibility' | 'interaction' | Partial<{
    visibility: boolean
    interaction: boolean
  }>
  /**
   * Escape hatch to disable `prefetch` attribute.
   */
  noPrefetch?: boolean
}

export interface LinkProps extends NuxtLinkProps {
  /**
   * The element or component this component should render as when not a link.
   * @defaultValue 'button'
   */
  as?: any
  /**
   * The type of the button when not a link.
   * @defaultValue 'button'
   */
  type?: ButtonHTMLAttributes['type']
  disabled?: boolean
  /** Force the link to be active independent of the current route. */
  active?: boolean
  /** Will only be active if the current route is an exact match. */
  exact?: boolean
  /** Allows controlling how the current route query sets the link as active. */
  exactQuery?: boolean | 'partial'
  /** Will only be active if the current route hash is an exact match. */
  exactHash?: boolean
  /** The class to apply when the link is inactive. */
  inactiveClass?: string
  custom?: boolean
  /** When `true`, only styles from `class`, `activeClass`, and `inactiveClass` will be applied. */
  raw?: boolean
  class?: HTMLAttributes['class']
}

export interface FooterColumnLink extends Omit<LinkProps, 'custom'> {
  label: string
  /**
   * @IconifyIcon
   */
  icon?: string
}

export interface FooterColumn<T extends FooterColumnLink = FooterColumnLink> {
  label: string
  children?: T[]
}

export interface FooterColumnsProps<T extends FooterColumnLink = FooterColumnLink> {
  /**
   * The element or component this component should render as.
   * @defaultValue 'div'
   */
  as?: any
  class?: any
  columns?: FooterColumn<T>[]
}

type SlotProps<T> = (props: { link: T, active: boolean }) => any

// export
interface FooterColumnsSlots<T extends FooterColumnLink = FooterColumnLink> {
  'left': (props?: object) => any
  'default': (props?: object) => any
  'right': (props?: object) => any
  'link': SlotProps<T>
  'link-leading': SlotProps<T>
  'link-label': SlotProps<T>
  'link-trailing': SlotProps<T>
}

const props = withDefaults(defineProps<FooterColumnsProps<T>>(), {
  as: 'nav',
})

const slots = defineSlots<FooterColumnsSlots<T>>()

function pickLinkProps(link: LinkProps & { [key: string]: any }) {
  const keys = Object.keys(link)

  const ariaKeys = keys.filter(key => key.startsWith('aria-'))
  const dataKeys = keys.filter(key => key.startsWith('data-'))

  const propsToInclude = ['active', 'activeClass', 'ariaCurrentValue', 'as', 'disabled', 'exact', 'exactActiveClass', 'exactHash', 'exactQuery', 'external', 'href', 'download', 'inactiveClass', 'noPrefetch', 'noRel', 'prefetch', 'prefetchedClass', 'rel', 'replace', 'target', 'to', 'type', 'title', 'onClick', ...ariaKeys, ...dataKeys]

  return reactivePick(link, ...propsToInclude)
}

const appConfig = {
  ui: {
    footerColumns: {
      slots: {
        // root: 'xl:grid xl:grid-cols-3 xl:gap-8',
        root: '',
        left: 'mb-10 xl:mb-0',
        // center: 'flex flex-col lg:grid grid-flow-col auto-cols-fr gap-8 xl:col-span-2',
        center: 'flex flex-col lg:grid grid-flow-col auto-cols-fr gap-8',
        right: 'mt-10 xl:mt-0',
        label: 'text-sm font-semibold',
        list: 'mt-6 space-y-4',
        item: 'relative',
        link: 'group text-sm flex items-center gap-1.5 focus-visible:outline-primary min-w-0',
        linkLeadingIcon: 'size-5 shrink-0',
        linkLabel: 'block truncate',
        linkLabelExternalIcon: 'size-3 absolute top-0 text-muted-foreground inline-block',
      },
      variants: {
        active: {
          true: {
            link: 'text-primary font-medium',
          },
          false: {
            link: [
              'text-muted-foreground hover:text-primary',
              'transition-colors',
            ],
          },
        },
      },
    },
  },
}
</script>

<template>
  <Primitive :as="as" :class="cn(appConfig.ui.footerColumns.slots.root, props.class)">
    <div v-if="!!slots.left" :class="appConfig.ui.footerColumns.slots.left">
      <slot name="left" />
    </div>

    <div v-if="!!slots.default || columns?.length" :class="appConfig.ui.footerColumns.slots.center">
      <slot>
        <div v-for="(column, index) in columns" :key="index">
          <h3 :class="appConfig.ui.footerColumns.slots.label">
            {{ column.label }}
          </h3>

          <ul :class="appConfig.ui.footerColumns.slots.list">
            <li v-for="(link, linkIndex) in column.children" :key="linkIndex" :class="appConfig.ui.footerColumns.slots.item">
              <UiLink v-slot="{ active, ...slotProps }" v-bind="pickLinkProps(link)" custom>
                <UiLinkBase v-bind="slotProps" :class="cn(appConfig.ui.footerColumns.slots.link, appConfig.ui.footerColumns.variants.active[(!!active).toString() as 'true' | 'false'].link, link.class)">
                  <slot name="link" :link="(link as T)" :active="active">
                    <slot name="link-leading" :link="(link as T)" :active="active">
                      <Icon v-if="link.icon" :name="link.icon" :class="appConfig.ui.footerColumns.slots.linkLeadingIcon" :active />
                    </slot>

                    <span v-if="link.label || !!slots['link-label']" :class="appConfig.ui.footerColumns.slots.linkLabel" :active>
                      <slot name="link-label" :link="(link as T)" :active="active">
                        {{ (link as T).label }}
                      </slot>

                      <Icon v-if="link.target === '_blank'" name="lucide:external-link" :class="appConfig.ui.footerColumns.slots.linkLabelExternalIcon" :active />
                    </span>

                    <slot name="link-trailing" :link="(link as T)" :active="active" />
                  </slot>
                </UiLinkBase>
              </UiLink>
            </li>
          </ul>
        </div>
      </slot>
    </div>

    <div v-if="!!slots.right" :class="appConfig.ui.footerColumns.slots.right">
      <slot name="right" />
    </div>
  </Primitive>
</template>
